import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { Upload, FileSpreadsheet, Download, Check, X, Loader2 } from 'lucide-react';

interface ProcessedFile {
  id: string;
  name: string;
  status: 'processing' | 'done' | 'error';
  cleanedCount: number;
  blob?: Blob;
  outputName?: string;
  errorMessage?: string;
}

const ExportCleanerPage: React.FC = () => {
  const [files, setFiles] = useState<ProcessedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const extractFromSVG = (val: any): any => {
    if (val == null || val === '') return val;
    const s = String(val).trim();
    if (!s.includes('svg')) return val;

    // Strategy: find ALL <text ...>VALUE</text> blocks, then pick the display value.
    // Power BI SVGs typically have:
    //   1) A transparent/hidden sorting text (font-size='0.001' or fill='transparent')
    //   2) A visible display value (the one we want)
    //   3) Sometimes a count/secondary value
    // We grab the last non-transparent, non-sorting text content.

    const textBlocks = [...s.matchAll(/<text[^>]*>([^<]*)<\/text>/g)];
    if (textBlocks.length === 0) return val;

    // Filter out transparent/sorting texts
    const visible = textBlocks.filter((m) => {
      const tag = m[0];
      const content = m[1].trim();
      if (!content) return false;
      if (tag.includes("fill='transparent'") || tag.includes('fill="transparent"')) return false;
      if (tag.includes("font-size='0.001'") || tag.includes('font-size="0.001"')) return false;
      if (tag.includes("opacity='0'") || tag.includes('opacity="0"')) return false;
      return true;
    });

    if (visible.length === 0) return val;

    // For LTV cohort SVGs (dominant-baseline): first visible = value, second = count
    // For variance SVGs (alignment-baseline): typically one visible = the value
    // Take the first visible text as the display value
    const display = visible[0][1].trim();

    const cleaned = display.replace(/[£$€,% ]/g, '');
    const num = Number(cleaned);
    if (!isNaN(num) && cleaned !== '') return num;
    return display;
  };

  const parsePBIExport = async (arrayBuffer: ArrayBuffer): Promise<{ sheets: Record<string, any[][]>; totalCleaned: number }> => {
    const zip = await JSZip.loadAsync(arrayBuffer);
    const parser = new DOMParser();

    const wbXmlFile = zip.file('xl/workbook.xml');
    if (!wbXmlFile) {
      throw new Error('Invalid Excel file: workbook.xml not found');
    }
    const wbXml = await wbXmlFile.async('string');
    const wbDoc = parser.parseFromString(wbXml, 'application/xml');
    const sheetEls = wbDoc.querySelectorAll('sheet');

    const relsXmlFile = zip.file('xl/_rels/workbook.xml.rels');
    if (!relsXmlFile) {
      throw new Error('Invalid Excel file: workbook.xml.rels not found');
    }
    const relsXml = await relsXmlFile.async('string');
    const relsDoc = parser.parseFromString(relsXml, 'application/xml');
    const rels: Record<string, string> = {};
    relsDoc.querySelectorAll('Relationship').forEach((r) => {
      const id = r.getAttribute('Id');
      const target = r.getAttribute('Target');
      if (id && target) {
        rels[id] = target;
      }
    });

    const sheets: Record<string, any[][]> = {};
    let totalCleaned = 0;

    for (const sheetEl of Array.from(sheetEls)) {
      const name = sheetEl.getAttribute('name') || 'Sheet';
      const rId = sheetEl.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id');
      const target = (rId && rels[rId]) || 'worksheets/sheet1.xml';
      const path = target.startsWith('/') ? target.slice(1) : 'xl/' + target;

      const sheetXmlFile = zip.file(path);
      if (!sheetXmlFile) continue;

      const sheetXml = await sheetXmlFile.async('string');
      const sheetDoc = parser.parseFromString(sheetXml, 'application/xml');

      const ns = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
      const rows = sheetDoc.getElementsByTagNameNS(ns, 'row');
      const data: any[][] = [];

      for (let ri = 0; ri < rows.length; ri++) {
        const row = rows[ri];
        const cells = row.getElementsByTagNameNS(ns, 'c');
        const rowData: any[] = [];

        for (let ci = 0; ci < cells.length; ci++) {
          const cell = cells[ci];
          const type = cell.getAttribute('t');
          let value: any = null;

          if (type === 'inlineStr') {
            const is = cell.getElementsByTagNameNS(ns, 't');
            if (is.length > 0) value = is[0].textContent;
          } else {
            const v = cell.getElementsByTagNameNS(ns, 'v');
            if (v.length > 0) value = v[0].textContent;
          }

          if (value && typeof value === 'string' && value.includes('svg')) {
            value = extractFromSVG(value);
            totalCleaned++;
          }

          rowData.push(value);
        }
        data.push(rowData);
      }

      sheets[name] = data;
    }

    return { sheets, totalCleaned };
  };

  const processFile = useCallback(async (file: File) => {
    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    setFiles((prev) => [
      {
        id: fileId,
        name: file.name,
        status: 'processing',
        cleanedCount: 0,
      },
      ...prev,
    ]);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const { sheets, totalCleaned } = await parsePBIExport(arrayBuffer);

      const wb = XLSX.utils.book_new();
      for (const [name, data] of Object.entries(sheets)) {
        const ws = XLSX.utils.aoa_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, ws, name);
      }

      const outName = file.name.replace(/\.xlsx?$/i, '_cleaned.xlsx');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? {
                ...f,
                status: 'done',
                cleanedCount: totalCleaned,
                blob,
                outputName: outName,
              }
            : f
        )
      );
    } catch (err: any) {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? {
                ...f,
                status: 'error',
                errorMessage: err.message || 'Unknown error',
              }
            : f
        )
      );
      console.error(err);
    }
  }, []);

  const handleFiles = useCallback((fileList: FileList) => {
    for (const file of Array.from(fileList)) {
      if (!file.name.match(/\.xlsx?$/i)) continue;
      processFile(file);
    }
  }, [processFile]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
    e.target.value = '';
  };

  const handleDownload = (file: ProcessedFile) => {
    if (!file.blob || !file.outputName) return;
    
    const a = document.createElement('a');
    a.href = URL.createObjectURL(file.blob);
    a.download = file.outputName;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    }, 100);
  };

  return (
    <div className="min-h-screen bg-[#f4f5f7] flex flex-col items-center py-12 px-5">
      <div className="w-full max-w-[600px]">
        {/* Header */}
        <div className="flex items-center gap-3.5 mb-2">
          <h1 className="text-xl font-bold tracking-tight text-gray-900">
            Export Cleaner
          </h1>
        </div>
        <p className="text-sm text-gray-500 leading-relaxed mb-7">
          Drop your exported .xlsx files below. Custom-coded cells will be replaced with clean numbers.
        </p>

        {/* Drop zone */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-[10px] p-11 text-center cursor-pointer
            transition-all duration-200 bg-white shadow-sm
            ${isDragOver ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-primary hover:bg-primary/5'}
          `}
        >
          <div className="w-12 h-12 mx-auto mb-3.5 bg-primary/10 rounded-xl flex items-center justify-center pointer-events-none">
            <Upload className="w-[22px] h-[22px] text-primary" />
          </div>
          <div className="text-[15px] font-semibold text-gray-900 mb-1 pointer-events-none">
            Drop .xlsx files here
          </div>
          <div className="text-[13px] text-gray-500 pointer-events-none">
            or click to browse
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".xlsx,.xls"
            onChange={handleInputChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            title="Upload Excel files"
            aria-label="Upload Excel files"
          />
        </div>

        {/* File list */}
        {files.length > 0 && (
          <div className="mt-4 flex flex-col gap-2">
            {files.map((file) => (
              <div
                key={file.id}
                className={`
                  flex items-center gap-3 p-3.5 px-4 bg-white border rounded-[10px] shadow-sm
                  transition-all duration-200
                  ${file.status === 'done' ? 'border-green-200 bg-green-50/50' : ''}
                  ${file.status === 'error' ? 'border-red-200 bg-red-50/50' : ''}
                  ${file.status === 'processing' ? 'border-gray-200' : ''}
                `}
              >
                {/* File icon */}
                <div
                  className={`
                    w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
                    ${file.status === 'done' ? 'bg-green-100' : ''}
                    ${file.status === 'error' ? 'bg-red-100' : ''}
                    ${file.status === 'processing' ? 'bg-primary/10' : ''}
                  `}
                >
                  {file.status === 'processing' && (
                    <FileSpreadsheet className="w-[18px] h-[18px] text-primary" />
                  )}
                  {file.status === 'done' && (
                    <Check className="w-[18px] h-[18px] text-green-600" strokeWidth={2.5} />
                  )}
                  {file.status === 'error' && (
                    <X className="w-[18px] h-[18px] text-red-600" strokeWidth={2.5} />
                  )}
                </div>

                {/* File info */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {file.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {file.status === 'processing' && 'Processing...'}
                    {file.status === 'done' && (
                      <>
                        <span className="text-green-600 font-bold">
                          {file.cleanedCount.toLocaleString()}
                        </span>{' '}
                        custom-coded cells cleaned
                      </>
                    )}
                    {file.status === 'error' && `Error: ${file.errorMessage}`}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex-shrink-0">
                  {file.status === 'processing' && (
                    <Loader2 className="w-5 h-5 text-primary animate-spin" />
                  )}
                  {file.status === 'done' && (
                    <button
                      onClick={() => handleDownload(file)}
                      className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white px-4 py-1.5 rounded-full text-[13px] font-semibold transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Instructions */}
        <div className="mt-7 p-5 px-6 bg-white border border-gray-200 rounded-[10px] shadow-sm">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3.5">
            How to use
          </h3>
          <ol className="text-sm text-gray-500 leading-8 list-decimal pl-5">
            <li className="pl-1">Export a table data to Excel</li>
            <li className="pl-1">Drop the file above</li>
            <li className="pl-1">
              Click{' '}
              <span className="font-bold text-gray-900">
                Download
              </span>{' '}
              to save the cleaned version
            </li>
            {/* <li className="pl-1">Open in Excel — all custom-coded cells are now plain numbers</li> */}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ExportCleanerPage;
